import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { ApiContext } from '../../../../context/MyContext';
import { useUtils } from '../../../../context/UtilsContext';
import { useWorkoutData } from '../../core/providers/WorkoutDataProvider';
import { getWorkoutPeriods } from '../../core/utils/workoutPeriods';
import { buildWorkoutDurationSummary } from '../../core/utils/workoutDuration';
import {
  fetchWorkoutGroupsBundle,
  periodWorkoutsFromBundle,
} from '../../core/api/workoutApi';
import WorkoutPeriodEditor from './WorkoutPeriodEditor';

const EMPTY_GROUP_TYPES = [];
const EMPTY_EVENT_TYPES = [];
const EMPTY_DURATION = {
  totalSeconds: 0,
  totalLabel: '',
  workSeconds: 0,
  setRestSeconds: 0,
  restAfterSeconds: 0,
  totalCalories: 0,
};

export default function WorkoutPeriodRows({
  context,
  templateId = 0,
  planId = 0,
  clientId = null,
  setAlertMessage,
  setAlertType,
  initialBundle = null,
  onDurationChange,
  copyTargetDays = null,
}) {
  const { get, post } = useContext(ApiContext);
  const { sessionUtils } = useUtils();
  const { workoutCommonUtils } = useWorkoutData();
  // Keep a stable reference — `|| []` would allocate a new array every render.
  const workout_group_types =
    workoutCommonUtils?.workout_group_types || EMPTY_GROUP_TYPES;
  // getWorkoutPeriods always returns a new array; memoize so applyBundle/refreshBundle stay stable.
  const periods = useMemo(
    () => getWorkoutPeriods(workout_group_types),
    [workout_group_types]
  );

  const eventTypes = useMemo(
    () => sessionUtils?.event_types?.[0]?.event_types || EMPTY_EVENT_TYPES,
    [sessionUtils?.event_types]
  );

  const [workoutBundle, setWorkoutBundle] = useState(initialBundle);
  const [bundleLoading, setBundleLoading] = useState(!initialBundle);
  const [workoutGroupTypeIds, setWorkoutGroupTypeIds] = useState({});
  const seededInitialRef = useRef(false);

  const applyBundle = useCallback(
    (bundle) => {
      setWorkoutBundle(bundle);
      if (!bundle) {
        setWorkoutGroupTypeIds({});
        return;
      }
      const ids = {};
      periods.forEach((period) => {
        const { workoutGroupTypeIds: groupIds } = periodWorkoutsFromBundle(
          bundle,
          workout_group_types,
          period.key
        );
        if (groupIds) ids[period.key] = groupIds;
      });
      setWorkoutGroupTypeIds(ids);
    },
    [periods, workout_group_types]
  );

  const refreshBundle = useCallback(async () => {
    if (!workoutCommonUtils) return null;
    setBundleLoading(true);
    try {
      const bundle = await fetchWorkoutGroupsBundle(get, post, {
        context,
        templateId,
        planId,
        clientId,
        eventTypes,
        workout_group_types,
      });
      applyBundle(bundle);
      return bundle;
    } catch (error) {
      console.error('Failed to load workout bundle:', error);
      applyBundle(null);
      return null;
    } finally {
      setBundleLoading(false);
    }
  }, [
    get,
    post,
    context,
    templateId,
    planId,
    clientId,
    eventTypes,
    workout_group_types,
    workoutCommonUtils,
    applyBundle,
  ]);

  // Seed from prefetch once for instant paint, then refresh from the server.
  // Only re-run when the template/plan identity changes — not when applyBundle
  // identity churns (that was stomping local reorder edits).
  useEffect(() => {
    seededInitialRef.current = false;
  }, [context, templateId, planId, clientId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (initialBundle && !seededInitialRef.current) {
        seededInitialRef.current = true;
        applyBundle(initialBundle);
        setBundleLoading(false);
      }
      if (!cancelled) await refreshBundle();
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh on identity / utils ready only
  }, [context, templateId, planId, clientId, initialBundle, workoutCommonUtils]);

  const updateGroupIds = useCallback((periodKey, ids) => {
    setWorkoutGroupTypeIds((prev) => {
      const existing = prev[periodKey];
      if (
        existing &&
        existing.id === ids?.id &&
        existing.workout_group_type_id === ids?.workout_group_type_id
      ) {
        return prev;
      }
      return { ...prev, [periodKey]: ids };
    });
  }, []);

  const durationSummary = useMemo(
    () =>
      buildWorkoutDurationSummary(
        workoutBundle,
        periods,
        workout_group_types,
        periodWorkoutsFromBundle
      ),
    [workoutBundle, periods, workout_group_types]
  );

  useEffect(() => {
    onDurationChange?.(durationSummary);
  }, [durationSummary, onDurationChange]);

  useEffect(() => {
    return () => onDurationChange?.(EMPTY_DURATION);
  }, [onDurationChange]);

  if (periods.length === 0) {
    return (
      <p className="text-sm text-gray-500 py-8 text-center">
        No workout periods configured. Add workout group types in reference data.
      </p>
    );
  }

  if (bundleLoading && !workoutBundle) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {periods.map((period) => (
        <WorkoutPeriodEditor
          key={period.key}
          periodKey={period.key}
          periodLabel={period.label}
          context={context}
          templateId={templateId}
          planId={planId}
          clientId={clientId}
          setAlertMessage={setAlertMessage}
          setAlertType={setAlertType}
          updateWorkoutGroupTypeIds={updateGroupIds}
          workoutGroupTypeIds={workoutGroupTypeIds}
          workoutBundle={workoutBundle}
          refreshBundle={refreshBundle}
          copyTargetDays={copyTargetDays}
        />
      ))}
    </div>
  );
}
